import {
  useController,
  type Control,
  type FieldValues,
  type FieldPath,
  type UseControllerProps,
} from 'react-hook-form'
import { Form } from '@design-systems/canvas-ui'
import React, { useCallback } from 'react'

type Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName
  control: Control<TFieldValues>
  rules?: UseControllerProps<TFieldValues, TName>['rules']
} & Omit<
  React.ComponentProps<typeof Form.Field>,
  'onChange' | 'onBlur' | 'value' | 'name'
>

const ControlledFormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  ...formFieldProps
}: Props<TFieldValues, TName>) => {
  const { field } = useController({
    control,
    name,
    rules,
  })

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      if (formFieldProps.type === 'select') {
        // type casting to match what canvas ui actually does
        const option = event as unknown as { id: string } | undefined
        field.onChange(option?.id)
      } else {
        field.onChange(event)
      }
    },
    [field, formFieldProps.type],
  )

  const addOnProps =
    // if the field is a checkbox, we need to pass isChecked with value otherwise it sees it as uncontrolled -> to controlled
    formFieldProps.type === 'checkbox' ? { isChecked: field.value } : {}

  return (
    <Form.Field
      onChange={handleChange}
      onBlur={field.onBlur}
      value={field.value}
      name={field.name}
      {...addOnProps}
      {...formFieldProps}
    />
  )
}

export default ControlledFormField
